<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "ApplicationDocumentsModal",
  props: { application: { type: Object, required: true } },
  emits: ["close", "saved"],
  data: () => ({
    selectedDocument: null,
    documentType: "",
    sittingNumber: 1,
    reason: "",
    file: null,
    action: "request",
    saving: false,
    error: "",
    openingVersionId: null,
  }),
  computed: {
    documents() {
      return this.application.documents || [];
    },
    documentVersions() {
      return this.application.documentVersions || [];
    },
    activeRequests() {
      return (this.application.replacementRequests || []).filter((item) =>
        ["open", "uploaded"].includes(item.status)
      );
    },
  },
  methods: {
    label(document) {
      const labels = {
        profile_image: "Profile Image",
        jamb_result: "JAMB Result",
        birth_certificate: "Birth Certificate",
        o_level_result: "O-Level Result",
      };
      return `${labels[document.documentType] || document.documentType}${
        document.sittingNumber ? ` · Sitting ${document.sittingNumber}` : ""
      }`;
    },
    selectDocument(document) {
      this.selectedDocument = document;
      this.documentType = document.documentType;
      this.sittingNumber = document.sittingNumber || 1;
      this.reason = "";
      this.file = null;
      this.error = "";
    },
    onFile(event) {
      this.file = event.target.files?.[0] || null;
    },
    async openVersion(version) {
      this.openingVersionId = version._id;
      this.error = "";
      try {
        const response = await axios.get(version.contentUrl, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Unable to open the document version.";
      } finally {
        this.openingVersionId = null;
      }
    },
    async cancelRequest(request) {
      const confirmation = await Swal.fire({
        title: "Cancel replacement request?",
        input: "textarea",
        inputLabel: "Reason",
        inputValidator: (value) =>
          !String(value || "").trim() || String(value).trim().length < 5
            ? "Enter a reason of at least 5 characters"
            : undefined,
        showCancelButton: true,
        confirmButtonText: "Cancel request",
      });
      if (!confirmation.isConfirmed) return;
      this.saving = true;
      this.error = "";
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/document-replacement-requests/${request._id}/cancel`,
          { reason: String(confirmation.value).trim() }
        );
        this.$emit("saved", { keepOpen: true });
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to cancel the request.";
      } finally {
        this.saving = false;
      }
    },
    async accept(document) {
      this.saving = true;
      this.error = "";
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/documents/${document._id}/review`,
          { status: "accepted" }
        );
        this.$emit("saved", { keepOpen: true });
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to accept the document.";
      } finally {
        this.saving = false;
      }
    },
    async submit() {
      this.error = "";
      if (!this.documentType) this.error = "Select a document.";
      else if (this.reason.trim().length < 5)
        this.error = "Enter a reason of at least 5 characters.";
      else if (this.action === "upload" && !this.file)
        this.error = "Select the replacement file.";
      if (this.error) return;
      this.saving = true;
      try {
        if (this.action === "request") {
          await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/document-replacement-requests`,
            {
              applicationDocumentId: this.selectedDocument?._id || null,
              documentType: this.documentType,
              sittingNumber:
                this.documentType === "o_level_result"
                  ? this.sittingNumber
                  : null,
              reason: this.reason.trim(),
            }
          );
        } else {
          const body = new FormData();
          body.append("file", this.file);
          body.append("documentType", this.documentType);
          if (this.documentType === "o_level_result")
            body.append("sittingNumber", this.sittingNumber);
          body.append("reason", this.reason.trim());
          await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/documents/replace`,
            body,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        }
        this.$emit("saved", { keepOpen: true });
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to update the document.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div class="action-backdrop" role="dialog" aria-modal="true">
    <div class="action-dialog bg-white rounded shadow-lg">
      <header class="border-bottom p-3 d-flex justify-content-between">
        <div>
          <h5 class="fw-bold mb-1">Manage documents</h5>
          <small class="text-muted">{{ application.applicationNumber }}</small>
        </div>
        <button
          class="btn-close"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        ></button>
      </header>
      <div class="action-body p-3">
        <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
        <div v-if="activeRequests.length" class="alert alert-info small">
          <strong>Active requests:</strong>
          <div
            v-for="request in activeRequests"
            :key="request._id"
            class="d-flex justify-content-between align-items-center gap-2 mt-1"
          >
            <span>
              {{ request.documentType
              }}<span v-if="request.sittingNumber">
                (sitting {{ request.sittingNumber }})</span
              >
              · {{ request.status }}
            </span>
            <button
              v-if="request.status === 'open'"
              class="btn btn-sm btn-outline-secondary"
              type="button"
              :disabled="saving"
              @click="cancelRequest(request)"
            >
              Cancel
            </button>
          </div>
        </div>
        <div class="list-group mb-4">
          <button
            v-for="document in documents"
            :key="document._id"
            type="button"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            :class="{ active: selectedDocument?._id === document._id }"
            @click="selectDocument(document)"
          >
            <span
              ><strong>{{ label(document) }}</strong
              ><small
                class="d-block"
                :class="
                  selectedDocument?._id === document._id
                    ? 'text-white-50'
                    : 'text-muted'
                "
                >{{ document.originalName }} · version
                {{ document.version || 1 }}</small
              ></span
            >
            <span
              class="badge"
              :class="
                document.status === 'accepted'
                  ? 'text-bg-success'
                  : document.status === 'rejected'
                  ? 'text-bg-danger'
                  : 'text-bg-secondary'
              "
              >{{ document.status }}</span
            >
          </button>
        </div>
        <div v-if="documentVersions.length" class="mb-4">
          <h6 class="fw-bold">Previous retained versions</h6>
          <div
            v-for="version in documentVersions"
            :key="version._id"
            class="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
          >
            <div>
              <strong
                >{{ label(version) }} · version {{ version.version }}</strong
              >
              <small class="d-block text-muted">
                {{ version.originalName }} · {{ version.replacementSource }} ·
                {{ version.replacementReason }}
              </small>
            </div>
            <button
              class="btn btn-sm btn-outline-secondary"
              type="button"
              :disabled="openingVersionId === version._id"
              @click="openVersion(version)"
            >
              <span
                v-if="openingVersionId === version._id"
                class="spinner-border spinner-border-sm"
              ></span>
              <span v-else>Open</span>
            </button>
          </div>
        </div>
        <div class="border rounded p-3">
          <h6 class="fw-bold">Document correction</h6>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Document type</label
              ><select
                v-model="documentType"
                class="form-select"
                :disabled="Boolean(selectedDocument)"
              >
                <option value="">Select</option>
                <option value="profile_image">Profile image</option>
                <option value="jamb_result">JAMB result</option>
                <option value="birth_certificate">Birth certificate</option>
                <option value="o_level_result">O-Level result</option>
              </select>
            </div>
            <div v-if="documentType === 'o_level_result'" class="col-md-3">
              <label class="form-label">Sitting</label
              ><select
                v-model.number="sittingNumber"
                class="form-select"
                :disabled="Boolean(selectedDocument)"
              >
                <option :value="1">Sitting 1</option>
                <option :value="2">Sitting 2</option>
              </select>
            </div>
            <div class="col-12">
              <div class="btn-group">
                <input
                  id="request-action"
                  v-model="action"
                  value="request"
                  type="radio"
                  class="btn-check"
                /><label for="request-action" class="btn btn-outline-primary"
                  >Request applicant upload</label
                ><input
                  id="upload-action"
                  v-model="action"
                  value="upload"
                  type="radio"
                  class="btn-check"
                /><label for="upload-action" class="btn btn-outline-primary"
                  >Staff upload</label
                >
              </div>
            </div>
            <div v-if="action === 'upload'" class="col-12">
              <label class="form-label">Replacement file</label
              ><input
                type="file"
                class="form-control"
                :accept="
                  documentType === 'profile_image'
                    ? 'image/jpeg,image/png'
                    : 'application/pdf'
                "
                @change="onFile"
              /><small class="text-muted"
                >PDF up to 2 MB; profile image up to 2 MB before
                processing.</small
              >
            </div>
            <div class="col-12">
              <label class="form-label">Reason *</label
              ><textarea
                v-model.trim="reason"
                class="form-control"
                rows="3"
                maxlength="1000"
              ></textarea>
            </div>
          </div>
          <div class="d-flex justify-content-between mt-3">
            <button
              v-if="selectedDocument && selectedDocument.status !== 'accepted'"
              class="btn btn-outline-success"
              type="button"
              :disabled="saving"
              @click="accept(selectedDocument)"
            >
              Accept current document</button
            ><span v-else></span
            ><button
              class="btn btn-primary"
              type="button"
              :disabled="saving"
              @click="submit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
              ></span
              >{{
                action === "request" ? "Send request" : "Upload replacement"
              }}
            </button>
          </div>
        </div>
      </div>
      <footer class="border-top p-3 text-end">
        <button
          class="btn btn-light"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        >
          Close
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.action-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(0, 0, 0, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.action-dialog {
  width: min(800px, 100%);
  max-height: 94vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.action-body {
  overflow-y: auto;
}
@media (max-width: 767.98px) {
  .action-backdrop {
    align-items: flex-start;
    overflow-y: auto;
  }
  .action-dialog {
    max-height: none;
    margin-block: 0.5rem;
  }
  .action-body {
    overflow: visible;
  }
}
</style>
